/**
 * Allow-list sanitizer for Privacy Policy / Terms HTML.
 *
 * Content pasted from Word or Google Docs arrives with inline styles, classes,
 * <font> tags and sometimes scripts. The website renders this HTML directly
 * (it runs its own sanitizer too), so we keep only structural markup here:
 * the result is predictable on the site and safe if the site's pass is ever
 * skipped.
 */

const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "hr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "strike",
  "ul",
  "ol",
  "li",
  "a",
  "blockquote",
  "details",
  "summary",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
])

// Dropped together with everything inside them.
const DROPPED_WITH_CONTENT = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "noscript",
  "template",
  "head",
  "meta",
  "link",
  "title",
  "svg",
  "math",
])

const ALIGN_VALUES = new Set(["left", "right", "center", "justify", "start", "end"])

const isSafeHref = (href) => {
  const value = String(href || "").trim()
  if (!value) return false
  if (value.startsWith("#") || value.startsWith("/")) return true
  try {
    const url = new URL(value, window.location.origin)
    return ["http:", "https:", "mailto:", "tel:"].includes(url.protocol)
  } catch {
    return false
  }
}

const copySafeAttributes = (source, target) => {
  const tag = target.tagName.toLowerCase()

  const dir = source.getAttribute("dir")
  if (dir === "rtl" || dir === "ltr" || dir === "auto") {
    target.setAttribute("dir", dir)
  }

  // execCommand("justify*") writes alignment as an inline style; keep only
  // that one property so the admin's alignment survives.
  const align = (source.style?.textAlign || source.getAttribute("align") || "")
    .toLowerCase()
    .trim()
  if (ALIGN_VALUES.has(align) && tag !== "a") {
    target.setAttribute("style", `text-align: ${align}`)
  }

  if (tag === "a") {
    const href = source.getAttribute("href")
    if (isSafeHref(href)) {
      target.setAttribute("href", href.trim())
      target.setAttribute("target", "_blank")
      target.setAttribute("rel", "noopener noreferrer")
    }
  }

  if (tag === "td" || tag === "th") {
    for (const name of ["colspan", "rowspan"]) {
      const n = Number(source.getAttribute(name))
      if (Number.isInteger(n) && n > 1) target.setAttribute(name, String(n))
    }
  }
}

const cleanChildren = (sourceParent, targetParent, doc) => {
  sourceParent.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      targetParent.appendChild(doc.createTextNode(node.textContent))
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const tag = node.tagName.toLowerCase()
    if (DROPPED_WITH_CONTENT.has(tag)) return

    if (ALLOWED_TAGS.has(tag)) {
      const el = doc.createElement(tag)
      copySafeAttributes(node, el)
      cleanChildren(node, el, doc)
      targetParent.appendChild(el)
      return
    }

    // contentEditable wraps new lines in <div>; treat them as paragraphs.
    if (tag === "div" || tag === "section" || tag === "article") {
      const el = doc.createElement("p")
      copySafeAttributes(node, el)
      cleanChildren(node, el, doc)
      targetParent.appendChild(el)
      return
    }

    // Unknown inline wrappers (span, font, o:p, ...): keep their text only.
    cleanChildren(node, targetParent, doc)
  })
}

/** Fix the <p><p>…</p></p> nesting the <div> → <p> mapping can produce. */
const unwrapNestedBlocks = (root) => {
  root.querySelectorAll("p").forEach((p) => {
    const hasBlockChild = Array.from(p.children).some((c) =>
      ["p", "ul", "ol", "table", "blockquote", "details", "h1", "h2", "h3", "h4", "h5", "h6", "hr"].includes(
        c.tagName.toLowerCase()
      )
    )
    if (hasBlockChild) p.replaceWith(...p.childNodes)
  })
}

export const sanitizePolicyHtml = (html) => {
  if (!html) return ""
  try {
    const parsed = new DOMParser().parseFromString(String(html), "text/html")
    const out = document.implementation.createHTMLDocument("")
    const root = out.createElement("div")
    cleanChildren(parsed.body, root, out)
    unwrapNestedBlocks(root)
    return root.innerHTML
  } catch {
    return ""
  }
}

/**
 * True when pasted plain text is HTML source (e.g. copied from a code block or
 * an .html file) rather than prose, so Write mode can render it instead of
 * showing the tags as literal text.
 */
export const looksLikeHtmlSource = (text) => {
  const value = String(text || "").trim()
  if (!value.startsWith("<")) return false
  const tags = value.match(/<\/?([a-z][a-z0-9]*)\b[^>]*>/gi) || []
  const known = tags.filter((tag) => {
    const name = tag.replace(/^<\/?/, "").match(/^[a-z0-9]+/i)?.[0]?.toLowerCase()
    return ALLOWED_TAGS.has(name) || name === "div" || name === "span"
  })
  return known.length >= 2 && /<\/[a-z][a-z0-9]*>/i.test(value)
}

/** True when the HTML has no visible text (e.g. "<p><br></p>"). */
export const isPolicyHtmlEmpty = (html) => {
  if (!html) return true
  const div = document.createElement("div")
  div.innerHTML = html
  return !div.textContent.trim() && !div.querySelector("hr, table")
}
